import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { NewPasswordModule, ProgressSpinnerModule, SvgIconModule } from '@twaice-fe/frontend/shared/ui-components';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

const { authActions } = actions;
const { authSelectors } = selectors;

enum ComponentState {
  DEFAULT,
  PASSWORD_CHANGE_IN_PROGRESS,
  PASSWORD_CHANGE_COMPLETE,
}

@Component({
  selector: 'twaice-fe-password-change',
  templateUrl: './password-change.component.html',
  imports: [
    CommonModule,
    SvgIconModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzFormModule,
    NzInputModule,
    FormsModule,
    NzMenuModule,
    NzCheckboxModule,
    NzMenuModule,
    NewPasswordModule,
    ProgressSpinnerModule,
  ],
})
export class PasswordChangeComponent implements OnInit, OnDestroy {
  passwordChangeForm: UntypedFormGroup = this.fb.group({
    currentPassword: new UntypedFormControl('', Validators.required),
  });

  forceChange = false;

  // eslint-disable-next-line @typescript-eslint/naming-convention -- exposing enum to template
  ComponentStateEnum = ComponentState;
  componentState: ComponentState = ComponentState.DEFAULT;

  error: string;
  valueChangeSubscription: Subscription;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private activatedRoute: ActivatedRoute
  ) {
    this.store
      .select(authSelectors.getUser)
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        if (user) {
          this.componentState = ComponentState.DEFAULT;
        }
      });

    this.store
      .select(authSelectors.getErrors)
      .pipe(takeUntilDestroyed())
      .subscribe(({ changePasswordError }) => {
        this.error = changePasswordError?.message;
        // We mark it as pristine so we know to reset it when the user changes the value
        this.passwordChangeForm.markAsPristine();
        this.valueChangeSubscription = this.passwordChangeForm.valueChanges
          .pipe(first(() => this.error && !this.passwordChangeForm.pristine))
          .subscribe(() => {
            // Resetting the error display when the user changes the value
            this.error = null;
          });

        this.componentState = ComponentState.DEFAULT;
      });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.forceChange = !!params.forceChange;
    });
  }

  ngOnDestroy() {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormGroupGenerated(newPasswordFormGroup: UntypedFormGroup) {
    this.passwordChangeForm.setControl('newPassword', newPasswordFormGroup);
  }

  setNewPassword() {
    // Resetting potential previous server side error
    this.error = '';
    /*
     * When the user tries to send out the form, we want to show errors also for form items in the child components
     * therefore we need to mark all the items touched, not just user the ones used in the current component
     * which is the default form behaviour
     * */
    this.passwordChangeForm.markAllAsTouched();
    if (this.passwordChangeForm.valid) {
      this.componentState = ComponentState.PASSWORD_CHANGE_IN_PROGRESS;

      this.store.dispatch(
        authActions.changePassword({
          oldPassword: this.passwordChangeForm.value.currentPassword,
          newPassword: this.passwordChangeForm.value.newPassword.password,
        })
      );
    }
  }
}
